import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t, errorText, money, dateTime } from '../i18n/index.ts';
import { api, newKey } from '../lib/api.ts';
import { Avatar, Dialog, ErrorView, Loading, Money, Status, useToast } from '../components/ui.tsx';
import { TabBar } from '../components/TabBar.tsx';
import { centsToInput, parseEuroInput } from '../../shared/money.ts';
import type { DebtView } from '../../server/domain/debts.ts';

type DebtList = { iOwe: DebtView[]; owedToMe: DebtView[]; totals: { iOweRemainingCents: number; owedToMeRemainingCents: number } };

export function formatPhone(e164: string): string {
  if (e164.startsWith('+358')) {
    const n = `0${e164.slice(4)}`;
    return `${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`.trim();
  }
  return e164;
}

export function useCopy() {
  const toast = useToast();
  return async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    toast(t('debts.copied', { what: label }));
  };
}

export function DebtsPage() {
  const q = useQuery({ queryKey: ['debts'], queryFn: () => api.get<DebtList>('/api/debts') });
  const [tab, setTab] = useState<'owe' | 'owed'>('owe');
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;
  const list = tab === 'owe' ? d.iOwe : d.owedToMe;
  return (
    <div className="page">
      <h1>{t('debts.title')}</h1>
      <p className="hint">{t('tournament.moneyNote')}</p>
      <TabBar
        group="debts"
        label={t('debts.title')}
        fill
        active={tab}
        items={[
          {
            key: 'owe',
            label: (
              <>
                {t('debts.iOwe')} <span className="num">{money(d.totals.iOweRemainingCents)}</span>
              </>
            ),
            onSelect: () => setTab('owe'),
          },
          {
            key: 'owed',
            label: (
              <>
                {t('debts.owedToMe')} <span className="num">{money(d.totals.owedToMeRemainingCents)}</span>
              </>
            ),
            onSelect: () => setTab('owed'),
          },
        ]}
      />
      <div role="tabpanel">
        {list.length === 0 ? (
          <div className="rows">
            <p className="empty">{tab === 'owe' ? t('debts.noneOwe') : t('debts.noneOwed')}</p>
          </div>
        ) : (
          <PersonGroups debts={list} side={tab} />
        )}
        <p className="hint" style={{ marginTop: 16 }}>
          {t('debts.noOffset')}
        </p>
      </div>
    </div>
  );
}

function PersonGroups({ debts, side }: { debts: DebtView[]; side: 'owe' | 'owed' }) {
  const groups = useMemo(() => {
    const map = new Map<string, DebtView[]>();
    for (const d of debts) {
      const other = side === 'owe' ? d.creditor : d.debtor;
      map.set(other.id, [...(map.get(other.id) ?? []), d]);
    }
    return [...map.values()]
      .map((list) => ({
        person: side === 'owe' ? list[0]!.creditor : list[0]!.debtor,
        phone: list.find((x) => x.counterpartyPhone)?.counterpartyPhone ?? null,
        remaining: list.reduce((s, x) => s + x.remainingCents, 0),
        list: [...list].sort((a, b) => Number(a.status === 'paid') - Number(b.status === 'paid') || b.createdAt - a.createdAt),
      }))
      .sort((a, b) => b.remaining - a.remaining);
  }, [debts, side]);
  const copy = useCopy();
  return (
    <>
      {groups.map((g) => (
        <section key={g.person.id} className="panel" aria-label={g.person.displayName} style={{ marginBottom: 16 }}>
          <div className="section-head" style={{ alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
              <Avatar name={g.person.displayName} url={g.person.avatarUrl} />
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0, overflowWrap: 'anywhere' }}>{g.person.displayName}</h2>
                {g.phone ? (
                  <div className="btn-row" style={{ gap: 6 }}>
                    <a href={`tel:${g.phone}`} className="num">
                      {formatPhone(g.phone)}
                    </a>
                    <button className="btn btn-small" onClick={() => copy(g.phone!.startsWith('+358') ? `0${g.phone!.slice(4)}` : g.phone!, t('debts.phone'))}>
                      {t('debts.copyPhone')}
                    </button>
                  </div>
                ) : (
                  <span className="hint">{t('debts.noPhone')}</span>
                )}
              </div>
            </div>
            <div className="stat debt-total">
              <span className="stat-label">{t('debts.remaining')}</span>
              <span className="stat-value">
                <Money cents={g.remaining} />
              </span>
            </div>
          </div>
          <ul className="rows">
            {g.list.map((d) => (
              <DebtRow key={d.id} debt={d} side={side} />
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}


function DebtRow({ debt, side }: { debt: DebtView; side: 'owe' | 'owed' }) {
  const [recording, setRecording] = useState(false);
  const [reversing, setReversing] = useState<DebtView['entries'][number] | null>(null);
  const copy = useCopy();
  const qc = useQueryClient();
  const toast = useToast();
  const notice = useMutation({
    mutationFn: () => api.post(`/api/debts/${debt.id}/paid-notice`),
    onSuccess: () => {
      toast(t('debts.noticeDone', { name: debt.creditor.displayName }));
      void qc.invalidateQueries({ queryKey: ['debts'] });
    },
    onError: (err) => toast(errorText(err), 'error'),
  });
  return (
    <li style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0 }}>
          <Link to={`/turnaukset/${debt.tournament.id}/tulokset`} className="row-title">
            {debt.tournament.name}
          </Link>{' '}
          {debt.kind === 'correction' && <span className="chip-tag">{t('debts.correction')}</span>}
          <div className="row-sub">{dateTime(debt.createdAt)}</div>
        </div>
        <Status kind={debt.status}>{t(`debts.status.${debt.status}`)}</Status>
      </div>
      <dl className="debt-amounts">
        <div>
          <dt>{t('debts.original')}</dt>
          <dd>
            <Money cents={debt.amountCents} />
          </dd>
        </div>
        <div>
          <dt>{t('debts.paid')}</dt>
          <dd>
            <Money cents={debt.paidCents} />
          </dd>
        </div>
        <div>
          <dt>{t('debts.remaining')}</dt>
          <dd>
            <strong>
              <Money cents={debt.remainingCents} />
            </strong>
          </dd>
        </div>
      </dl>
      {debt.remainingCents > 0 && (
        <div className="btn-row">
          {side === 'owe' && debt.appLink && (
            // Only opens the app; the number and amount have their own copy buttons.
            <a className="btn btn-brass btn-small mobile-only" href={debt.appLink}>
              {t('debts.openMobilePay')}
            </a>
          )}
          {side === 'owe' && debt.payLink && (
            <a className="btn btn-brass btn-small" href={debt.payLink} target="_blank" rel="noopener noreferrer">
              {t('debts.payMobilePay', { amount: money(debt.remainingCents) })}
            </a>
          )}
          {side === 'owe' && (
            <button className="btn btn-small" onClick={() => copy(centsToInput(debt.remainingCents), t('debts.amountWord'))}>
              {t('debts.copyAmount')}
            </button>
          )}
          {side === 'owed' && (
            <button className="btn btn-primary btn-small" onClick={() => setRecording(true)}>
              {t('debts.record')}
            </button>
          )}
        </div>
      )}
      {side === 'owe' && debt.remainingCents > 0 && (
        <div className="paid-notice">
          {debt.paidNoticeAt ? (
            <p className="hint" role="status">
              {t('debts.noticeSent', { time: dateTime(debt.paidNoticeAt) })}
            </p>
          ) : (
            <p className="hint">{t('debts.afterPaying')}</p>
          )}
          <button className="btn btn-small" disabled={notice.isPending} onClick={() => notice.mutate()}>
            {debt.paidNoticeAt ? t('debts.noticeAgain') : t('debts.noticePaid')}
          </button>
        </div>
      )}
      {side === 'owed' && debt.paidNoticeAt && (
        <p className="alert alert-ok" style={{ margin: '8px 0 0' }}>
          {t('debts.noticeReceived', { name: debt.debtor.displayName, time: dateTime(debt.paidNoticeAt) })}
        </p>
      )}
      <details style={{ marginTop: 10 }}>
        <summary>
          {t('debts.history')} ({debt.entries.length})
        </summary>
        {debt.entries.length === 0 ? (
          <p className="hint">{t('debts.noHistory')}</p>
        ) : (
          <ol className="log" style={{ paddingLeft: 0 }}>
            {debt.entries.map((e) => (
              <li key={e.id}>
                <span className="hint">{dateTime(e.createdAt)}</span>{' '}
                {e.kind === 'payment' ? (
                  <>
                    <span style={e.reversedBy ? { textDecoration: 'line-through' } : undefined}>{t('debts.payment', { amount: money(e.amountCents) })}</span>
                    {e.note && <span> – {e.note}</span>}
                    {e.reversedBy && <span className="hint"> ({t('debts.reversed')})</span>}
                  </>
                ) : (
                  <span className="negative">{t('debts.reversal', { amount: money(e.amountCents), note: e.note })}</span>
                )}{' '}
                <span className="hint">{t('debts.by', { name: e.createdBy.displayName })}</span>
                {side === 'owed' && e.kind === 'payment' && !e.reversedBy && (
                  <button className="btn btn-ghost btn-small" onClick={() => setReversing(e)}>
                    {t('debts.reverse')}
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
      </details>
      {recording && <RecordPaymentDialog debt={debt} onClose={() => setRecording(false)} />}
      {reversing && <ReverseDialog debt={debt} entry={reversing} onClose={() => setReversing(null)} />}
    </li>
  );
}

function RecordPaymentDialog({ debt, onClose }: { debt: DebtView; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const key = useRef(newKey()); // one key per dialog: a double click records once
  const cents = parseEuroInput(amount);
  const error = amount === '' ? null : cents === null || cents <= 0 ? t('debts.invalidAmount') : cents > debt.remainingCents ? t('debts.tooMuch', { amount: money(debt.remainingCents) }) : null;
  const m = useMutation({
    mutationFn: () => api.post(`/api/debts/${debt.id}/payments`, { amountCents: cents, note, idempotencyKey: key.current }),
    onSuccess: () => {
      toast(t('debts.recorded'));
      onClose();
    },
    onError: (e) => toast(errorText(e), 'error'),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['debts'] });
      void qc.invalidateQueries({ queryKey: ['home'] });
    },
  });
  return (
    <Dialog title={t('debts.recordTitle')} onClose={onClose}>
      <p>{t('debts.recordHelp', { name: debt.debtor.displayName })}</p>
      <p>
        {t('debts.remaining')}: <strong>{money(debt.remainingCents)}</strong>
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!error && cents && !m.isPending) m.mutate();
        }}
      >
        <div className="field">
          <label htmlFor="pay-amount">{t('debts.amount')}</label>
          <input id="pay-amount" className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} aria-invalid={!!error} />
          <button type="button" className="btn btn-small" style={{ alignSelf: 'flex-start' }} onClick={() => setAmount(centsToInput(debt.remainingCents))}>
            {t('debts.payAll', { amount: money(debt.remainingCents) })}
          </button>
          {error && (
            <span className="error-text" role="alert">
              {error}
            </span>
          )}
        </div>
        <div className="field">
          <label htmlFor="pay-note">{t('debts.note')}</label>
          <input id="pay-note" className="input" maxLength={300} placeholder={t('debts.notePlaceholder')} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <div className="btn-row">
          <button className="btn btn-primary" disabled={!cents || !!error || m.isPending}>
            {cents && !error ? t('debts.confirmRecord', { amount: money(cents) }) : t('debts.record')}
          </button>
          <button type="button" className="btn" data-close onClick={onClose}>
            {t('app.cancel')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function ReverseDialog({ debt, entry, onClose }: { debt: DebtView; entry: DebtView['entries'][number]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const key = useRef(newKey());
  const m = useMutation({
    mutationFn: () => api.post(`/api/debts/${debt.id}/entries/${entry.id}/reverse`, { reason, idempotencyKey: key.current }),
    onSuccess: () => {
      toast(t('debts.reversedDone'));
      onClose();
    },
    onError: (e) => toast(errorText(e), 'error'),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['debts'] }),
  });
  return (
    <Dialog title={t('debts.reverseTitle')} onClose={onClose}>
      <p>{t('debts.reverseHelp', { amount: money(entry.amountCents) })}</p>
      <div className="field">
        <label htmlFor="rev-reason">{t('debts.reason')}</label>
        <input id="rev-reason" className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
      </div>
      <div className="btn-row">
        <button className="btn btn-danger" disabled={reason.trim().length < 3 || m.isPending} onClick={() => m.mutate()}>
          {t('debts.reverse')}
        </button>
        <button className="btn" data-close onClick={onClose}>
          {t('app.cancel')}
        </button>
      </div>
    </Dialog>
  );
}
